/**
 * Determines if any user requires admin action for Regtank reattempt approval
 * Checks both onboarding and liveness verification status
 */

import { UserData } from "../types/org.type";
import {
  RegtankReattemptApprovalEnum,
  RegtankOnboardingFHStatusEnum,
  RegtankLivenessFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import {
  processOnboardingData,
  processLivenessData,
  rawOnboardingData,
  rawLivenessData,
} from "../Utils/getRegtankDetails";
import { sortByExpiryDateDesc } from "../Utils/regtankUtils";
import { toISOString } from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";

/**
 * Checks if user's Regtank onboarding requires review
 */
const requiresOnboardingReview = (user: UserData): boolean => {
  if (!user.regtankOnboarding || !user.regtankRequest?.length) {
    return false;
  }

  const sortedRequests = sortByExpiryDateDesc(user.regtankRequest);
  const mostRecent = sortedRequests[0];
  const expiredDate = toISOString(mostRecent.expiredDate);

  const onboardingData: rawOnboardingData[] = [{
    requestId: mostRecent.requestId,
    onboardingStatus: mostRecent.onboardingStatus,
    expiredDate,
    reattemptApproval: mostRecent.reattemptApproval,
  }];

  const result = processOnboardingData(onboardingData);
  return result.some(
    (x) => x.newStatus === RegtankOnboardingFHStatusEnum.PENDING_REVIEW
  );
};

/**
 * Checks if user's Regtank liveness verification requires review
 */
const requiresLivenessReview = (user: UserData): boolean => {
  if (!user.regtankLiveness?.length) {
    return false;
  }

  const sortedLiveness = sortByExpiryDateDesc(user.regtankLiveness);
  const mostRecent = sortedLiveness[0];
  const expiredDate = toISOString(mostRecent.expiredDate);

  const livenessData: rawLivenessData[] = [{
    sessionId: mostRecent.sessionId,
    livenessStatus: mostRecent.livenessStatus,
    expiredDate,
    numberOfLiveness: user.regtankLiveness.length - 1,
    reattemptApproval: mostRecent.reattemptApproval,
    submittedOn: toISOString(mostRecent.submittedDate),
    failureReason: mostRecent.reason,
  }];

  const result = processLivenessData(livenessData);
  return result.some(
    (x) => x.newStatus === RegtankLivenessFHStatusEnum.PENDING_REVIEW
  );
};

/**
 * Determines if any user requires reattempt approval action
 * @returns ACTION_REQUIRED if any user needs review, NO_ACTION_REQUIRED otherwise
 */
export const getRegtankReattemptStatus = (users: UserData[]) => {
  if (!users?.length) {
    return RegtankReattemptApprovalEnum.NO_ACTION_REQUIRED;
  }

  // Check if any user requires review (onboarding or liveness)
  const hasActionRequired = users.some(
    (user) => requiresOnboardingReview(user) || requiresLivenessReview(user)
  );

  return hasActionRequired
    ? RegtankReattemptApprovalEnum.ACTION_REQUIRED
    : RegtankReattemptApprovalEnum.NO_ACTION_REQUIRED;
};
